import React, { useState, useEffect, useMemo } from "react";
import { addCommas, bindClick, bindFormChange, fromUnix, getNow, getNowLocal } from '@arema/components/Util';
import { Stat, Header, Table, Icon, Groupper, Button, Input, Placeholder, Modal } from "react-frontier2";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Promoter } from "../../AdminClasses";
import API from "../../API";
import { DateTime } from "luxon";
import { Money } from "@arema/components";

enum Modals{
	FILTER = 1,
}

interface Props {
	promoter: Promoter,
	promoter_id?: number,
	onEditPromoter?: Function,
}

interface Balance {
	balance: number,
	events: number,
}

interface EventBalance{
	event_id: number,
	event_name: string,
	total_tickets: number,
	total_commission: number,
	total_payout: number,
	total_balance: number,
	download_url: string,
}

interface BalanceFilter{
	date_start: Date,
	date_end: Date,
	active: boolean,
}

const PAGE_SIZE = 20;

const PromoterBalanceList = (props: Props)=>{
	var { t } = useTranslation();
	var [balance, setBalance] = useState<Balance>(null);
	var [events, setEvents] = useState<EventBalance[]>(null);
	var [page, setPage] = useState<number>(0);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [filter, setFilter] = useState<BalanceFilter>({
		date_start: getNowLocal().minus({ day: 7 }).toJSDate(),
		date_end: getNowLocal().toJSDate(),
		active: false,
	});
	var [loadError, setLoadError] = useState<string>(null);

	var loadBalance = ()=>{
		API.getPromoterBalance(props.promoter_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setBalance(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error cargando las cuentas. (LCL-1)');
		});
	}

	var getEvents = (page: number, filter?: BalanceFilter)=>{
		setPage(0);
		var has_filter = filter && filter.active && filter.date_start && filter.date_end;

		API.getPromoterEventsBalance(props.promoter_id, null, has_filter ? filter?.date_start.getTime()/1000 : null, has_filter ? filter.date_end.getTime()/1000 : null, has_filter ? 0 : page*PAGE_SIZE, has_filter ? 999999 : PAGE_SIZE).then(res=>{
			if(res.error) return setLoadError(res.message);
			setEvents(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-PRMBLST-1' }));
		})
	}

	var now = useMemo(()=>{
		return getNowLocal();
	}, []);

	var filterEvents = ()=>{
		setEvents(null);
		getEvents(0, {
			...filter,
			active: true
		});
		setFilter({
			...filter,
			active: true
		});
		setShownModal(null);
	}

	useEffect(()=>{
		if(!balance){
			loadBalance();
		}
		if(!events){
			getEvents(0);
		}
	}, []);

	var clearFilter = ()=>{
		setFilter(({
			...filter,
			active: false,
		}));
		setEvents(null);
		getEvents(0);
		setPage(0);
	}

	var onFilterChange = bindFormChange(filter, setFilter);
	if(loadError) return <Header text="Error" subtext={loadError} />

	return <>
		<Header text="Estado de cuenta" />
		<Stat.Group style={{ maxWidth: 600, margin: 'auto' }}>
			<Stat label={<>
				Balance {filter && filter.active && (
					<Icon name="filter" />
				)}
			</>} value={balance ? addCommas(balance.balance) : (
				<Placeholder height={38} width={120} />
			)} />
			<Stat label={<>
				Eventos {filter && filter.active && (
					<Icon name="filter" />
				)}
			</>} value={balance ? addCommas(balance.events, false) : (
				<Placeholder height={38} width={20} />
			)} />
		</Stat.Group>
		
		<Groupper fitted title={props.promoter?.promoter_name} style={{ maxWidth: 1000 }} titleRight={<>
			{!!filter && filter.active && (
				<Button icon size="tiny" color="black" iconName="filter-circle-xmark" onClick={clearFilter} style={{ marginRight: 5 }} />
			)}
			<Button size="tiny" text="Filtrar" color="blue" iconName="filter" onClick={bindClick(setShownModal, Modals.FILTER)} />
		</>}>
			{!!filter?.active && (
				<Table fitted>
					<Table.Row header>
						<Table.Cell colSpan={100} style={{ textAlign: 'center' }}>
							Mostrando eventos: {DateTime.fromJSDate(filter.date_start).toFormat('dd/MMM/yy')} <Icon name="arrow-right" /> {DateTime.fromJSDate(filter.date_end).toFormat('dd/MMM/yy')}
						</Table.Cell>
					</Table.Row>
				</Table>
			)}
			<Table fitted style={{ borderBottomLeftRadius: 8, borderBottomRightRadius: 8, marginTop: filter && filter.active ? -1 : undefined }}>
				<Table.Head
					collapsingIndexes={[0, 2, 3, 4, 5, 6]}
					data={[['ID', 'Nombre', 'Promotor', 'Servicio', 'Liquidado', 'Total', <Icon name="ellipsis-h" />]]}
				/>
				<Table.Body>
					{!!events && events.length==0 && (
						<Table.Cell empty row colSpan={100}>
							{filter && filter.active ? (
								'No se encontraron eventos con el filtro aplicado'
							) : page==0 ? (
								'El promotor o tiene eventos activos'
							) : `La página ${page+1} no tiene eventos`}
						</Table.Cell>
					)}
					{events ? events.map(a=>(
						<Table.Row
							key={`EVBL-${a.event_id}`}
							collapsingIndexes={[0, 2, 3, 4, 5, 6]}
							data={[
								a.event_id,
								a.event_name,
								<Money value={a.total_tickets || 0} />,
								<Money value={a.total_commission || 0} />,
								<Money value={a.total_payout || 0} />,
								<Money value={a.total_balance || 0} />,
								<Button size="tiny" icon iconName="ellipsis-h" menu={[
									{ text: 'Reporte aforo', iconName: 'file-download', as: Link, to: a.download_url, target: '_blank' },
									{ text: 'Ver evento', iconName: 'eye', as: Link, to: `/events/${a.event_id}/balance${filter ? `?ds=${filter.date_start}&de=${filter.date_end}` : ''}`, target: '_blank' },
								]} />
							]}
						/>
					)) : new Array(5).fill(0).map((a,i)=>(
						<Table.Row
							key={`PLHD-${i}`}
							collapsingIndexes={[0, 2, 3, 4, 5, 6]}
							data={[
								<Placeholder width={20} />,
								<Placeholder width={100} />,
								<Placeholder width={50} />,
								<Placeholder width={50} />,
								<Placeholder width={50} />,
								<Placeholder width={50} />,
								''
							]}
						/>
					))}
				</Table.Body>
			</Table>
		</Groupper>

		<Modal open={shownModal===Modals.FILTER} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Filtrar eventos</Modal.Header>
			<Modal.Content>
				<Input label="Fecha inicio" calendar={{
					mode: 'date',
					date: filter?.date_start,
					maxDate: now.toJSDate(),
					onSelected: onFilterChange('date_start')
				}} />
				<Input label="Fecha fin" calendar={{
					mode: 'date',
					date: filter?.date_end,
					maxDate: now.toJSDate(),
					onSelected: onFilterChange('date_end')
				}} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text={t('common.close')} onClick={bindClick(setShownModal)} />
				<Button text={t('common.filter')} onClick={filterEvents} color="black" />
			</Modal.Actions>
		</Modal>
	</>
}

export default PromoterBalanceList;